<template>
    <div class="navigation-tab-child_root" :class="{isSticky: tab.sticky}" :data-placement="placement">
        <div v-if="tab.separator === 'top' && haveSiblings" class="separator"></div>
        <Component
            :is="getComponentType"
            :class="{
                isActive,
                isFull: isDisplayModeFull,
                isReduced: isDisplayModeReduced,
                isAlwaysDropdown,
                isAnchoredBottom,
                haveSeparator: tab.separator === 'top' && haveSiblings,
            }"
            class="navigation-tab-child flex"
            :data-testid="tab.id"
            v-bind="getComputedAttributes"
            :aria-label="tab.title"
            @click="handleClick"
        >
            <div class="navigation-tab-child_title-wrapper flex" :style="tab.customStyle">
                <div class="navigation-tab-child_title-wrapper_notifications-container">
                    <div v-if="tab.icons?.prefixIcon" class="prefix-icon flex">
                        <Component :is="tab.icons.prefixIcon" />
                    </div>
                    <span>{{ tab.title }}</span>
                    <NotificationBadge v-if="tab.notificationsCount" :count="getAllNotificationsCount" :offset="0" />
                </div>
            </div>
        </Component>
    </div>
</template>

<script setup lang="ts">
    import type {AnchorHTMLAttributes} from 'vue';
    import {computed, toRefs, toValue} from 'vue';
    import type {Placement} from '@floating-ui/dom';
    import {NotificationBadge} from '@navbar-logged/features/notifications';
    import {storeToRefs} from 'pinia';
    import {useAsideMenuStore} from '../aside-menu.store';
    import type {NavigationTab} from '../aside-menu.types';
    import {useDisplayModeStore} from '../stores';

    const props = defineProps<{
        /** Main tab object */
        tab: NavigationTab;
        /** Does tab is part of a children group */
        haveSiblings: boolean;
        isAnchoredBottom?: boolean;
        isAlwaysDropdown?: boolean;
        selector?: boolean;
        placement: Placement;
    }>();

    const {tab, isAnchoredBottom} = toRefs(props);
    const asideMenuStore = useAsideMenuStore();
    const displayModeStore = useDisplayModeStore();
    const {currentActiveRoute} = storeToRefs(asideMenuStore);
    const {isDisplayModeFull, isDisplayModeReduced} = storeToRefs(displayModeStore);

    const hoverColor = computed(() => tab.value.customHoverColor ?? `var(--joy-color-neutral-20)`);

    const getNotificationsCount = computed(() => tab.value?.notificationsCount ?? 0);
    const getAllNotificationsCount = computed(() => getNotificationsCount.value);

    /** Dynamic component type */
    const getComponentType = computed<'div' | 'a'>(() => (tab.value.url ? 'a' : 'div'));
    const getComputedAttributes = computed<Partial<AnchorHTMLAttributes>>(() => {
        if (tab.value.url) {
            return {
                href: tab.value.url.href,
                ...(tab.value.url.blank && {
                    target: '_blank',
                    rel: 'noopener noreferer',
                }),
                ...tab.value.customAttrs,
            };
        } else {
            return {};
        }
    });

    // Status
    const isActive = computed<boolean>(() => {
        if (props.selector && props.tab.isSelected) {
            return toValue(props.tab.isSelected);
        } else {
            return currentActiveRoute.value === tab.value.id;
        }
    });

    // - Events
    function handleClick() {
        if (tab.value.action && typeof tab.value.action === 'function') {
            tab.value.action();
        }
    }
</script>

<style lang="scss" scoped>
    .navigation-tab-child_root {
        padding-top: 1px;
        &.isSticky {
            position: sticky;
            background-color: var(--joy-color-neutral-0);

            &[data-placement$='end'],
            &[data-placement$='top'] {
                bottom: 0px;
            }
        }
    }

    .navigation-tab-child {
        flex-flow: row nowrap;
        align-items: center;
        text-decoration: none;
        background-color: var(--joy-color-neutral-0);
        color: var(--joy-color-neutral-50);
        transition:
            background-color 0.15s ease-out,
            padding 0.3s ease;
        flex: 0 0 auto;
        padding: 2px 0;
        cursor: default;

        $r: &;

        &_title-wrapper {
            height: 48px;
            flex: 1 1 auto;
            border-radius: var(--joy-core-radius-3);
            align-items: center;
            padding: 0 var(--joy-core-spacing-4);
            padding-left: var(--joy-core-spacing-9);
            line-height: 1;
            cursor: pointer;
            transition:
                box-shadow 0.2s ease,
                background-color 0.2s ease;

            &_notifications-container {
                position: relative;
                display: flex;
                max-width: 100%;
                flex-flow: row nowrap;
                align-items: center;

                span {
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    max-width: 100%;
                    display: inline-block;
                    line-height: 1.3;
                }

                .prefix-icon {
                    margin-right: var(--joy-core-spacing-2);
                    width: 24px;
                    display: flex;
                    justify-content: center;
                }

                .alt-icon {
                    margin-left: var(--joy-core-spacing-3);
                }
            }

            &:active {
                box-shadow: 0 0 5px inset rgba(30, 30, 30, 0.1);
            }
        }

        &.isReduced,
        &.isAlwaysDropdown {
            color: var(--joy-color-neutral-50);

            #{$r}_title-wrapper {
                padding-left: var(--joy-core-spacing-6);
                padding-right: var(--joy-core-spacing-6);
            }
        }

        &.haveSeparator {
            padding: 4px 0 2px 0;
        }

        &.isActive {
            #{$r}_title-wrapper {
                background-color: var(--joy-color-secondary-10);
                font-weight: var(--joy-font-weight-bold);
            }
        }

        &.isFull:not(.isAlwaysDropdown) {
            #{$r}_title-wrapper {
                font-size: var(--joy-font-size-primary-300);
            }
        }

        &:not(.isReduced, .isAlwaysDropdown) {
            border-radius: var(--joy-core-radius-0) !important;
        }

        &:not(.isActive) {
            #{$r}_title-wrapper:hover {
                background-color: v-bind(hoverColor);
            }
        }
    }

    .separator {
        height: 1px;
        width: 100%;
        margin: 0 auto;
        background-color: var(--joy-color-neutral-20);
        transition:
            margin var(--joy-transition-duration-default) ease,
            width var(--joy-transition-duration-default) ease;
        flex: 0 0 auto;
    }
</style>
